import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  Divider,
} from '@mui/material';
import { PopupDialog, LookupRadio, LookupListItem } from '../../../../components/common';
import { type ProveedorFiltered } from '../types/proveedor';
import { useProveedorStore } from '../store';

interface ProveedorSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (proveedor: ProveedorFiltered) => void;
  title?: string;
}

const ProveedorSelectionModal: React.FC<ProveedorSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Seleccionar Proveedor'
}) => {
  const [selectedProveedor, setSelectedProveedor] = useState<ProveedorFiltered | null>(null);

  const {
    filteredProveedores,
    loading,
    error,
    fetchFilteredProveedores,
    clearError
  } = useProveedorStore();

  useEffect(() => {
    if (open) {
      fetchFilteredProveedores();
      setSelectedProveedor(null);
    }
  }, [open, fetchFilteredProveedores]);

  const handleSelect = () => {
    if (selectedProveedor) {
      onSelect(selectedProveedor);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedProveedor(null);
    onClose();
  };

  const handleProveedorClick = (proveedor: ProveedorFiltered) => {
    setSelectedProveedor(proveedor);
  };

  const handleCloseError = () => {
    clearError();
  };

  return (
    <PopupDialog
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="md"
      primaryAction={{
        label: 'Seleccionar',
        onClick: handleSelect,
        disabled: !selectedProveedor || loading,
        loading: loading,
        variant: 'contained',
        color: 'primary'
      }}
      secondaryAction={{
        label: 'Cancelar',
        onClick: handleClose,
        variant: 'outlined',
        color: 'inherit'
      }}
    >
      {error && (
        <Alert
          severity="error"
          onClose={handleCloseError}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
          {filteredProveedores.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              No hay proveedores disponibles
            </Typography>
          ) : (
            <List>
              {filteredProveedores.map((proveedor, index) => (
                <React.Fragment key={proveedor.idPersona}>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{ p: 0 }}
                      onClick={() => handleProveedorClick(proveedor)}
                      selected={selectedProveedor?.idPersona === proveedor.idPersona}
                    >
                      <LookupRadio
                        checked={selectedProveedor?.idPersona === proveedor.idPersona}
                        value={proveedor.idPersona}
                      />
                      <LookupListItem
                        mainText={`${proveedor.idPersona} - ${proveedor.razonSocial}`}
                        secondaryText={proveedor.identificacion}
                        sx={{ justifyContent: 'space-between' }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < filteredProveedores.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      )}
    </PopupDialog>
  );
};

export default ProveedorSelectionModal;